import React, { useState, useRef, useEffect, forwardRef } from "react";
import * as d3 from "d3";
import styled from "styled-components";
import { getDateWithOffset } from "common/api";
import NVLineChart from "../NVLineChartLive";
import Legend from "./Legend";
import {
  formatDate,
  getItemsUpdate,
  getTooltipTitle,
  MARGIN_FACET_CHART,
  MARGIN_DPI_CHART,
  CursorLine,
} from "views/Statistics/SubscriberDetail/livemetrics.utils";
import colors from "./colors";
import colorsDark from "./colorsDark";

const Graph = styled.div`
  position: relative;
`;

const TileContainer = styled.div`
  position: relative;
  min-height: 210px;
  @media print {
    & {
      height: 8cm;
      &.can-compress {
        height: auto;
      }
      width: 100%;
      display: block:
    }
  }
  display: flex;
  flex-direction: column;
  & > h4 {
    text-align: center;
    margin: 5px 0 15px 0;
  }
  & > .graph {
    flex: 1 1 100%;
    .nv-y.nv-axis .nv-axisMaxMin {
      display: none;
    }

    svg {
      height: 190px;
      overflow: visible;

      .nv-axislabel{
        transform: translate(20px, 0) rotate(-90deg);
      }

      .nv-scatterWrap {
        display: none;
      }
    }
  }
  &.spread {
    justify-content: space-between;
    padding: 1em;
    align-content: flex-start;
  }
  &.can-compress {
    min-height: 0;
  }
  .input-group {
    max-width: 20ch;
  }

  .border{
    position: absolute;
    background-color: transparent;

    &.top{
      width: 100%;
      height: 70px;
      top: 0;
      left: 0;
    }

    &.right{
      width: 24px;
      height: 100%;
      top: 0;
      right: 0;
    }

    &.bottom{
      width: 100%;
      height: 30px;
      bottom: 0;
      left: 0;
    }

    &.left{
      width: 30px;
      height: 100%;
      top: 0;
      left: 0;
    }
  }
`;

const StyledTooltipContainer = styled.div`
  position: absolute;
  z-index: 10000;
  text-align: left;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  padding: 0 5px;
  width: 190px;

  .title {
    color: black;
    margin-bottom: 0px;
  }

  .field-container {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .field {
      display: flex;
      gap: 5px;
      align-items: center;
      justify-content: space-between;

      .left-side {
        display: flex;
        gap: 5px;
        align-items: center;
      }

      .square {
        width: 12px;
        min-width: 12px;
        height: 12px;
        border: 1px solid #999;
        border-radius: 6px;
      }

      .label,
      .value {
        font-family: Arial, sans-serif;
        font-size: 13px;
        margin-bottom: 0px;
        color: black;
      }

      .label {
        font-weight: normal;
      }

      .value {
        text-align: right;
      }
    }
  }
`;

const StyledLegendContainer = styled.div`
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: end;
  padding-right: 25px;
  top: 36px;
  }
`;

const Tooltip = ({
  dataPoint,
  mouseX,
  mouseY,
  filteredFields,
  yTooltipFormat,
  title,
}) => {
  const isRightSide = mouseX < 175;
  const offset = isRightSide ? 55 : -190;

  return (
    <StyledTooltipContainer
      style={{
        top: mouseY + 45 + "px",
        left: mouseX + offset + "px",
      }}
      className="tooltip-live"
    >
      <label className="title">{formatDate(title)}</label>
      <div className="field-container">
        {filteredFields.map((field, index) => (
          <div className="field" key={field.label}>
            <div className="left-side">
              <div
                className="square"
                style={{ backgroundColor: field.color }}
              />
              <label className="label"> {field.label} </label>
            </div>
            <label className="value">
              {dataPoint && dataPoint[field.name] != undefined
                ? yTooltipFormat(dataPoint[field.name])
                : ""}
            </label>
          </div>
        ))}
      </div>
    </StyledTooltipContainer>
  );
};

const getHoveredTime = (mouseEvent, items) => {
  if (!mouseEvent || items.length === 0) {
    return null;
  }
  const {mouseXDPI, rangeDPI, mouseXFacet, rangeFacet} = mouseEvent;
  if(mouseXFacet!==null && rangeFacet){
    const xScale =  d3.scaleTime()
    .domain([items[0].time, items[items.length-1].time])
    .range([0, rangeFacet]);
    return xScale.invert(mouseXFacet);
  }
  if(mouseXDPI!==null && rangeDPI){
    const xScale =  d3.scaleTime()
    .domain([items[0].time, items[items.length-1].time])
    .range([0, rangeDPI]);
    return xScale.invert(mouseXDPI);
  }
  return null;
};

const getMouseX = (mouseEvent, range)=>{
  if (!mouseEvent) {
    return null;
  }
  const {mouseXDPI, mouseXFacet, rangeDPI} = mouseEvent;
  if(mouseXFacet){
    return mouseXFacet;
  }
  if(mouseXDPI){
    return (range/rangeDPI) * mouseXDPI;
  }
  return null;
}

export const getZeroValues = (lastValue, newValue) => {
  let zeroValues = [];
  if (newValue.time.getTime() - lastValue.time.getTime() > 2000) {
    const zeroValueTemplate = Object.keys(newValue).reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});
    for (
      let time = lastValue.time.getTime() + 1000;
      time < newValue.time.getTime() - 1000;
      time += 1000
    ) {
      zeroValues.push({ ...zeroValueTemplate, time: new Date(time) });
    }
  }
  return zeroValues;
};

export const FacetGraphLive = forwardRef(
  (
    {
      title,
      units = "",
      fields,
      yAxisFormat = null,
      value,
      resetSignal,
      onMouseEvent,
      mouseEvent,
      yTooltipFormat = (value) => value,
      timeOffset,
      timeWindow,
      paused = false,
      ...rest
    },
    ref
  ) => {
    const [items, setItems] = useState([]);
    const [window, setWindow] = useState(0);
    const [filteredFields, setFilteredFields] = useState(fields);
    const [fieldsFilter, setFieldsFilter] = useState(
      new Set(fields.map((field) => field.name))
    );
    const firstValue = useRef(false);
    const dataMap = useRef({});
    const [range, setRange] = useState(0);

    useEffect(() => {
      if (resetSignal) {
        dataMap.current = {};
        firstValue.current = false;
        setItems([]);
        setWindow(0);
      }
    }, [resetSignal]);

    useEffect(() => {
      if (!value) {
        return;
      }

      const now = getDateWithOffset(timeOffset);
      const newValue = { ...value };

      if (!firstValue.current) {
        firstValue.current = newValue;
      }

      const timeFrame =
        newValue.time.getTime() - firstValue.current.time.getTime();

      let { itemsUpdate, window: windowNew  } = getItemsUpdate(
        items,
        now,
        fields,
        timeOffset,
        timeFrame,
        timeWindow,
        newValue,
        window
      );
      setWindow(windowNew);
      const lastValue = itemsUpdate[itemsUpdate.length - 1];
      if (
        lastValue?.time &&
        newValue?.time &&
        formatDate(lastValue.time) !== formatDate(newValue.time)
      ) {
        delete dataMap.current[formatDate(itemsUpdate[0].time)];
        dataMap.current[formatDate(newValue.time)] = newValue;
        const zeroValues = getZeroValues(lastValue, newValue);

        setItems([...itemsUpdate.slice(1), ...zeroValues, newValue]);
      }
    }, [value]);

    useEffect(() => {
      if (fieldsFilter) {
        setFilteredFields(
          fields.filter((field) => fieldsFilter.has(field.name))
        );
      }
    }, [fieldsFilter]);

    function removeTooltip() {
      onMouseEvent(null);
    }

    const hoveredTime = getHoveredTime(mouseEvent, items);
    const mouseX = getMouseX(mouseEvent, range);

    return (
      <TileContainer className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <Graph className="graph" ref={ref}>
          <NVLineChart
            margin={{
              top: 4,
              right: 40,
              bottom: 30,
              left: 60,
            }}
            items={items}
            xField="time"
            yAxisUnits={units}
            xAxisFormat={d3.timeFormat("%H:%M:%S")}
            yAxisTopGap={0.1}
            yAxisFormat={yAxisFormat}
            fields={filteredFields}
            {...rest}
            onHighlight={(e) => {
              if (e) {
                const newEvent = {
                  ...e,
                  rangeFacet: range,
                  mouseXFacet: e.mouseX,
                  mouseXDPI: null,
                };
                onMouseEvent(newEvent);
              }
            }}
            tooltipEnabled={false}
            showLegend={false}
            onRange={(rangeNew) => {
              setRange(rangeNew);
            }}
            renderRate={paused ? 0 : (timeWindow === 10 ? 2 : 1)}
          />
          {mouseEvent && (
            <CursorLine
              style={{
                left: mouseX + MARGIN_FACET_CHART.left,
                height: `calc(100% - ${
                  MARGIN_FACET_CHART.top + MARGIN_FACET_CHART.bottom
                }px)`,
                top: MARGIN_FACET_CHART.top,
              }}
            />
          )}
        </Graph>
        {mouseEvent && hoveredTime && (
          <Tooltip
            dataPoint={dataMap.current[formatDate(hoveredTime)]}
            mouseX={mouseX}
            mouseY={mouseEvent.mouseY}
            filteredFields={filteredFields}
            yTooltipFormat={yTooltipFormat}
            title={hoveredTime}
          />
        )}
        {mouseEvent && (
          <>
            <div
              className="border top"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
            <div
              className="border right"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
            <div
              className="border bottom"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
            <div
              className="border left"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
          </>
        )}

        {items.length > 0 && (
          <StyledLegendContainer className="legend-graph-live">
            <Legend
              fields={fields}
              onChange={(filter) => setFieldsFilter(filter)}
              value={fieldsFilter}
            />
          </StyledLegendContainer>
        )}
      </TileContainer>
    );
  }
);

FacetGraphLive.displayName = "FacetGraphLive";